"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.expressionTransform = exports.evaluate = exports.parse = exports.ExpCache = void 0;
const parser_1 = require("./parser");
const interpreter_1 = require("./interpreter/interpreter");
const store_1 = require("../exports/store");
const lexer_1 = require("./lexer");
const selectors_1 = require("../exports/store/selectors");
const model_1 = require("../model");
const basic_1 = require("./functions/basic");
const types_1 = require("../types");
const i18n_1 = require("../exports/i18n");
const lodash_1 = require("lodash");
const config_1 = require("../config");
const consts_1 = require("./consts");
function resolverWrapper(context) {
    /**
     * The role of the resolver is, given a fieldId, get the value from the context according to the string
     *params
     * @value field ID
     * @originValue: whether to return the value of the original cell
     */
    return (fieldId) => {
        /**
         * Check for reserved keywords when formulas are run in the lookUp field.
         * When the keyword is hit, return LookUpField's cellValueArray instead of cellValue
         */
        const hostField = context.field;
        const state = context.state;
        // TODO: The first phase of column permissions,
        // because the middle layer does not actively push data,
        // and getFieldMap actively masks the data, so here we temporarily use the datasheet to get the fieldmap directly
        const datasheet = (0, selectors_1.getDatasheet)(state, context.field.property.datasheetId);
        const fieldMap = datasheet.snapshot.meta.fieldMap;
        // const fieldMap = getFieldMap(state, context.field.property.datasheetId)!;
        if (hostField.type === types_1.FieldType.LookUp && fieldId === consts_1.ROLLUP_KEY_WORDS) {
            const flatCellValue = model_1.LookUpField.bindContext(hostField, state).getFlatCellValue(context.record.id, true);
            return model_1.LookUpField.bindContext(hostField, state).cellValueToArray(flatCellValue);
        }
        const fieldPermissionMap = store_1.Selectors.getFieldPermissionMap(state, datasheet.id);
        const fieldRole = store_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, fieldId);
        const field = fieldMap[fieldId];
        if (!field && !fieldRole) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.view_field_search_not_found_tip, {
                value: fieldId,
            }));
        }
        const record = context.record;
        const recordSnapshot = { meta: { fieldMap: fieldMap }, recordMap: { [record.id]: record } };
        const cellValue = (0, selectors_1.getCellValue)(state, recordSnapshot, record.id, fieldId, false, datasheet.id, true);
        // TODO what if field is undefined?
        // String type fields need special treatment. Convert Segment|id type to pure string;
        const fieldBasicValueType = model_1.Field.bindContext(field, state).basicValueType;
        // Currently "", [], false will be converted to null when getCellValue,
        // in order to ensure the correct calculation result of the formula, the boolean type needs to be converted null => false
        if (fieldBasicValueType === types_1.BasicValueType.Boolean) {
            return Boolean(cellValue);
        }
        if (fieldBasicValueType === types_1.BasicValueType.String) {
            // TODO what if field is undefined?
            return model_1.Field.bindContext(field, state).cellValueToString(cellValue);
        }
        if (fieldBasicValueType === types_1.BasicValueType.Array) {
            // TODO what if field is undefined?
            return model_1.Field.bindContext(field, state).cellValueToArray(cellValue);
        }
        return cellValue;
    };
}
// The buffer for the calculation result of the formula field. The input value is unchanged and does not need to be recalculated
class ExpCache {
    static set(datasheetId, fieldId, exp, ast) {
        return this.cache.set(datasheetId + fieldId + exp, ast);
    }
    static get(datasheetId, fieldId, exp) {
        return this.cache.get(datasheetId + fieldId + exp);
    }
    static has(datasheetId, fieldId, exp) {
        return this.cache.has(datasheetId + fieldId + exp);
    }
    static del(datasheetId, fieldId, exp) {
        return this.cache.delete(datasheetId + fieldId + exp);
    }
    static clearAll() {
        return this.cache.clear();
    }
}
exports.ExpCache = ExpCache;
ExpCache.cache = new Map();
function parse(expression, context, updateCache) {
    if (!expression || !expression.trim()) {
        return {
            error: new Error((0, i18n_1.t)(i18n_1.Strings.function_content_empty)),
        };
    }
    const field = context.field;
    const datasheetId = (field && field.property && field.property.datasheetId) || '';
    if (!updateCache && ExpCache.has(datasheetId, field.id, expression)) {
        return ExpCache.get(datasheetId, field.id, expression);
    }
    const lexer = new lexer_1.FormulaExprLexer(expression);
    if (lexer.errors.length) {
        ExpCache.set(datasheetId, field.id, expression, { error: lexer.errors[0] });
    }
    else {
        try {
            const ast = new parser_1.FormulaExprParser(lexer, context).parse();
            ExpCache.set(datasheetId, field.id, expression, { lexer, ast });
        }
        catch (e) {
            ExpCache.set(datasheetId, field.id, expression, { error: e });
        }
    }
    return ExpCache.get(datasheetId, field.id, expression);
}
exports.parse = parse;
/**
 * Pass in the expression and context, enter row evaluation
 * rootNode is the root of the abstract syntax tree
 * The interpreter is used to access the abstract syntax tree
 * @export
 * @param {string} expression
 * @param {*} context
 * @param {Resolver} [resolver]
 * @returns {any}
 */
function evaluate(expression, context, shouldThrow, forceThrow) {
    if (!expression) {
        return null;
    }
    const state = context.state;
    const fieldMap = (0, selectors_1.getFieldMap)(state, context.field.property.datasheetId);
    const fExpr = parse(expression, { field: context.field, fieldMap, state });
    if ('error' in fExpr) {
        if (forceThrow) {
            throw fExpr.error;
        }
        return null;
    }
    // console.log(util.inspect(fExpr, { showHidden: false, depth: null }));
    try {
        const resolverFn = resolverWrapper(context);
        const interpreter = new interpreter_1.Interpreter(resolverFn, context);
        const result = interpreter.visit(fExpr.ast);
        // Error for NaN/Infinite/-Infinite values
        if ((0, lodash_1.isNumber)(result) && !(0, lodash_1.isFinite)(result)) {
            throw new basic_1.FormulaBaseError('NaN');
        }
        return result;
    }
    catch (e) {
        if (shouldThrow) {
            throw e;
        }
        return e;
    }
}
exports.evaluate = evaluate;
// Convert an expression with fieldName as variable name to an expression with fieldId as variable name
function expressionTransform(expression, { fieldMap, fieldPermissionMap }, to) {
    if (!expression) {
        return expression;
    }
    const lexer = new lexer_1.FormulaExprLexer(expression);
    if (!lexer) {
        return '';
    }
    // Convert fieldMap and use name as key
    let revertFieldMap = fieldMap;
    if (to === 'id') {
        revertFieldMap = {};
        for (const key in fieldMap) {
            const field = fieldMap[key];
            revertFieldMap[field.name] = field;
        }
    }
    return lexer.fullMatches.reduce((str, token) => {
        let tokenValue = token.value;
        function getPureTokenValue() {
            const pureTokenValue = token.value.replace(/\\(.)/g, '$1');
            const field = revertFieldMap[pureTokenValue];
            // Convert the variable name according to the to parameter
            if (!field) {
                return tokenValue;
            }
            if (to === 'id') {
                return field.id;
            }
            const name = field.name.replace(/[{}\\]/g, '\\$&');
            // When name contains illegal parameters, {} wrapping is required
            if (/[/+\-|=*/><()（）!&%'"“”‘’^`~,，\s]/.test(name)) {
                return `{${name}}`;
            }
            console.log((0, i18n_1.t)(i18n_1.Strings.not_found_field_the_name_as, {
                value: pureTokenValue,
            }));
            return name;
        }
        function getTokenValue() {
            const pureTokenValue = tokenValue.slice(1, -1).replace(/\\(.)/g, '$1');
            const field = revertFieldMap[pureTokenValue];
            const fieldRole = store_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, pureTokenValue);
            if (fieldRole === config_1.ConfigConstant.Role.None) {
                return `{${(0, i18n_1.t)(i18n_1.Strings.crypto_field)}}`;
            }
            // Convert the variable name according to the 'to' parameter
            if (field) {
                return to === 'id' ? `{${field.id}}` : `{${field.name.replace(/[{}\\]/g, '\\$&')}}`;
            }
            console.log((0, i18n_1.t)(i18n_1.Strings.not_found_field_the_name_as, {
                value: pureTokenValue,
            }));
            return tokenValue;
        }
        if (token.type === lexer_1.TokenType.PureValue) {
            tokenValue = getPureTokenValue();
        }
        if (token.type === lexer_1.TokenType.Value) {
            tokenValue = getTokenValue();
        }
        return str + tokenValue;
    }, '');
}
exports.expressionTransform = expressionTransform;
//# sourceMappingURL=evaluate.js.map